import { isLocalMidnight, saveCountdown, type CompactUnit, type Countdown, type Kind } from './storage';
import { href } from './router';
import { uuid } from './id';

/// Picks up a "pending import" record dropped into localStorage by the /to
/// view's "Save to my countdowns" button (see functions/views/countdown.hbs),
/// persists it via Dexie, and replaceStates the URL to /c/<id> so the SPA
/// router lands on the detail view of the freshly imported countdown.
///
/// Runs from main.ts before mount so there's no flash of the home screen
/// between the redirect from /to and the detail view rendering.
///
/// On any malformed input we silently drop the record (already deleted from
/// localStorage by then) and return without redirecting; the user lands on
/// the regular PWA home.
export async function handlePendingImport(): Promise<void> {
  if (typeof localStorage === 'undefined') return;
  const raw = localStorage.getItem('pendingImport');
  if (!raw) return;
  localStorage.removeItem('pendingImport');

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return;
  }

  if (typeof parsed.title !== 'string' || typeof parsed.dueDate !== 'string') return;

  const id = uuid();
  const now = Date.now();
  const kind: Kind =
    parsed.kind === 'birthday' || parsed.kind === 'anniversary' ? parsed.kind : null;
  // iOS /to URLs don't carry compactDisplayUnit — default to Day, same as
  // Countdown.compactDisplayUnit's default in the Swift model.
  const compactUnit: CompactUnit =
    parsed.compactUnit === 'Year' ||
    parsed.compactUnit === 'Month' ||
    parsed.compactUnit === 'Week'
      ? parsed.compactUnit
      : 'Day';
  const record: Countdown = {
    id,
    title: parsed.title,
    emoji: typeof parsed.emoji === 'string' && parsed.emoji ? parsed.emoji : '🎉',
    dueDate: parsed.dueDate,
    // The /to page passes the link's flag; a page served before it existed
    // gets the midnight rule the page itself applied.
    allDay: typeof parsed.allDay === 'boolean' ? parsed.allDay : isLocalMidnight(new Date(parsed.dueDate)),
    colorHex: Number.isFinite(parsed.colorHex) ? Number(parsed.colorHex) : 0,
    kind,
    originYear:
      typeof parsed.originYear === 'number' && Number.isFinite(parsed.originYear)
        ? parsed.originYear
        : null,
    formatUnits: Number.isFinite(parsed.formatUnits) ? Number(parsed.formatUnits) : 0,
    compactUnit,
    countOnlyWeekdays: parsed.countOnlyWeekdays === true,
    createdAt: now,
    updatedAt: now,
  };

  try {
    await saveCountdown(record);
    history.replaceState(null, '', href(`/c/${id}`));
  } catch {
    // Persisting failed (private mode quota, IDB corruption, etc) — drop the
    // pending entry rather than retry-looping on every PWA load.
  }
}
